import { gridViewManager } from '@/utils/grid'
import { useFormatter } from '@api/useFormatter'
import { useMarkets } from '@api/useMarkets'
import { useValuation } from '@api/useValuation'
import { ActionButtons } from '@components/InventoryValuation/Costing/ActionButtons'
import { getColumnDefs } from '@components/InventoryValuation/Costing/columnDefs'
import { GraviGrid } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { getCurrentMarket } from '@utils/general'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const STORAGE_KEY = 'inventory-costing'

export function CostingGrid({ isFullWidth, setIsFullWidth, filters, setFilters, setSelectedICI, date, setHeaderData }) {
  const [selectors, setSelectors] = useState(['site', 'product'])
  const { getCostOverviewQuery } = useValuation()
  const { useMarketsQuery } = useMarkets()
  const { data: marketData } = useMarketsQuery()
  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  const { updateSetting } = useUserSettings()
  const getCurrentMarketId = () => {
    const currentMarketName = getCurrentMarket()
    if (currentMarketName && marketData) {
      const currentMarketId = marketData.rows?.find((market) => market.name === currentMarketName)?.id
      return currentMarketId
    }
    return ''
  }
  const { data: overviewData, isFetching } = getCostOverviewQuery(
    moment(date).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm:ss'),
    getCurrentMarketId()
  )

  useEffect(() => {
    if (overviewData?.selectors?.length) {
      const areEqual = overviewData.selectors.every((item, index) => item === selectors[index])
      if (!areEqual) setSelectors(overviewData.selectors)
    }
  }, [overviewData?.selectors])

  const gridAPIRef = useRef(null)
  const [loading, setLoading] = useState(false)

  const onDayChange = (date, dateString, add) => {
    setLoading(true)
    gridAPIRef.current.deselectAll()
    setFilters({
      ...filters,
      date,
    })
    setLoading(false)
  }
  const getRowId = useCallback((row) => row?.data?.id, [])
  const columnDefs = useMemo(() => getColumnDefs(selectors, formattedCurrency), [selectors, formattedCurrency])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      suppressRowDeselection: true,
      suppressCellFocus: true,
    }),
    []
  )
  return (
    <GraviGrid
      style={{ width: '100%', height: '100%;' }}
      rowData={overviewData?.rows}
      externalRef={gridAPIRef}
      storageKey={STORAGE_KEY}
      controlBarProps={{
        title: 'Costing',
        actionButtons: (
          <ActionButtons
            date={date}
            isFullWidth={isFullWidth}
            setIsFullWidth={setIsFullWidth}
            onDayChange={onDayChange}
          />
        ),
      }}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      loading={isFetching}
      rowSelection='single'
      onSelectionChanged={(props) => {
        const selected = props.api.getSelectedRows()[0]
        setSelectedICI(selected?.inventory_costing_identifier?.id)
        setHeaderData({
          site: selected?.inventory_costing_identifier?.site,
          product: selected?.inventory_costing_identifier?.product,
        })
      }}
      onFirstDataRendered={(params) => params.api.getDisplayedRowAtIndex(0).setSelected(true)}
      enableRangeSelection={false}
      onRowDataUpdated={(params) => {
        if (params.api.getSelectedRows().length === 0) {
          params.api.getDisplayedRowAtIndex(0)?.setSelected(true)
        }
      }}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
